import { type Component } from 'solid-js';
import { Show } from 'solid-js';
import Cards from '~/features/cards';
import { formatCurrency } from '~/lib/format';
import { summary } from '~/stores/calc-store';
import { portfolioYield, totalBookCost, totalIncome, totalValue } from '~/stores/portfolio-store';
import { totalRoc } from '~/stores/acb-store';
import Accounts from '~/features/accounts';
import Transactions from '~/features/transactions';
import HoldingPanel from '~/features/holding-panel';
import { closeTransactions, focusedAccountId, openAccountId, openHolding } from '~/stores/accounts-view-store';

// Holdings, with one account's blotter beside them when asked for.
//
// Hidden until then. The buys are how a position got there rather than something
// read every visit, and a panel of them permanently open spent a quarter of the
// page on a question nobody had asked. A quarter when it is open, not half: the
// holdings table carries a dozen columns and the blotter carries the entry row,
// so the space belongs to the one being read rather than the one typed into.
//
// The totals sit inside this column rather than across the top of the app. They
// are the book's figures, and they were on every page saying the same thing over
// a screen that had nothing to do with them — on Explore, a fund screener; on
// History, ten funds you may not own. Here they sit directly above the accounts
// they are the sum of, and stop at that column's edge.
// No gap between the cards and the list: the cards carry their own bottom rule
// now, and a 1px seam under a 1px border renders as 2px.
const AccountsPage: Component = () => (
    <>
        <div class='flex-1 min-w-0 flex flex-col'>
            {/* Hidden while one account fills the page: they are the totals
                across every account, and a book-wide figure over a single
                account reads as that account's. */}
            <Show when={focusedAccountId() == null}>
            <div class='shrink-0'>
                <Cards
                    figures={{
                        incomePerYear: totalIncome(),
                        netPerYear: summary().net,
                        yieldPct: portfolioYield(),
                        value: totalValue(),
                        // Cost base rather than book cost: return of capital has
                        // already come out of it, so it is what the position
                        // actually counts as.
                        valueNote: `${formatCurrency(Math.max(0, totalBookCost() - totalRoc()))} cost base`,
                    }}
                />
            </div>
            </Show>
            <div class='flex-1 min-h-0'>
                <Accounts />
            </div>
        </div>
        {/* One panel, two things that can be in it — see accounts-view-store.
            Opening either closes the other, so there is never a question of
            which is on screen. */}
        <Show when={openAccountId()}>
            <div class='w-1/4 shrink-0'>
                <Transactions accountId={openAccountId()!} onClose={closeTransactions} />
            </div>
        </Show>
        <Show when={openHolding()}>
            <div class='w-1/4 shrink-0'>
                <HoldingPanel accountId={openHolding()!.accountId} symbol={openHolding()!.symbol} />
            </div>
        </Show>
    </>
);

export default AccountsPage;
